/**
 * Shim for next/link that provides compatible link behavior for non-Next.js environments.
 * Uses standard anchor elements with href for navigation.
 */
import React from 'react';

interface LinkProps {
    href: string;
    children: React.ReactNode;
    className?: string;
    target?: string;
    rel?: string;
    prefetch?: boolean;
    replace?: boolean;
    scroll?: boolean;
    shallow?: boolean;
    passHref?: boolean;
    locale?: string | false;
    legacyBehavior?: boolean;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Next.js Link shim - renders as a standard anchor element.
 * All Next.js-specific props (prefetch, replace, scroll, shallow, passHref, locale, legacyBehavior)
 * are accepted but ignored since they don't apply in non-Next.js environments.
 */
function Link({
    href,
    children,
    className,
    target,
    rel,
    onClick,
    // Next.js specific props - destructured to exclude from ...rest
    prefetch,
    replace,
    scroll,
    shallow,
    passHref,
    locale,
    legacyBehavior,
    ...rest
}: LinkProps) {
    // These Next.js-specific props are intentionally ignored in this shim
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ignored = { prefetch, replace, scroll, shallow, passHref, locale, legacyBehavior };
    return (
        <a
            href={href}
            className={className}
            target={target}
            rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
            onClick={onClick}
            {...rest}
        >
            {children}
        </a>
    );
}

export default Link;
